import React from 'react';
import { Button, Card, Col, Dropdown, Row } from 'react-bootstrap';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import '../../../form/CardForm.scss';
import { screeningClearanceRulesAnalysisApi } from '../../../apis';
import Has from '../../../auth/Has';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../../feedback/feedbackReducer';
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { DropdownWrapper } from '../../../common/DropdownWrapper';
import InfoSection from '../../../form/InfoSection';
import { InternalApiScreeningClearanceRulesAnalysis } from '../../../api-internal';
import {
  CAN_ADD_SCREENING_CLEARANCE_RULES_ANALYSIS,
  CAN_DELETE_SCREENING_CLEARANCE_RULES_ANALYSIS,
  CAN_MODIFY_SCREENING_CLEARANCE_RULES_ANALYSIS,
} from '../../../auth/permissions';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { parseErrorMessage } from '../../../common/errors/helpers';

const validationSchema = Yup.object().shape(
  {
    name: Yup.string().required('Required'),
  },
  []
);

export interface ScreeningClearanceRulesAnalysisInfoFormValues {
  name: string;
  description: string | undefined;
}

interface Props {
  analysis: InternalApiScreeningClearanceRulesAnalysis;
  editing: boolean;
  updateAnalysis: any;
  setEditing: any;
}

export const ScreeningClearanceRulesAnalysisInfo = ({
  analysis,
  updateAnalysis,
  editing,
  setEditing,
}: Props): React.JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const initialValues: ScreeningClearanceRulesAnalysisInfoFormValues = {
    name: analysis.name || '',
    description: analysis.description || '',
  };

  const deleteAnalysis = async (id: number) => {
    dispatch(
      showConfirmationModal({
        title: 'Are you sure?',
        confirmationText: 'Yes, delete it!',
        cancelText: 'No, keep it',
        onConfirm: async () => {
          try {
            await screeningClearanceRulesAnalysisApi.deleteAnalysis({
              id,
            });
            navigate('/screening/clearance-rules');
          } catch (_) {
            dispatch(
              showFeedback({
                message: 'Oops, something went wrong!',
                success: false,
              })
            );
          }
        },
      })
    );
  };

  const submit = async (
    formValues: ScreeningClearanceRulesAnalysisInfoFormValues,
    { setStatus, setSubmitting }: FormikHelpers<ScreeningClearanceRulesAnalysisInfoFormValues>
  ) => {
    try {
      const apiAnalysis = {
        ...analysis,
        name: formValues.name,
        description: formValues.description,
      };
      if (analysis.id) {
        await screeningClearanceRulesAnalysisApi.updateAnalysis({
          id: analysis.id,
          internalApiScreeningClearanceRulesAnalysis: apiAnalysis,
        });
        updateAnalysis(apiAnalysis);
      } else {
        const response: InternalApiScreeningClearanceRulesAnalysis =
          await screeningClearanceRulesAnalysisApi.addAnalysis({
            internalApiScreeningClearanceRulesAnalysis: apiAnalysis,
          });
        navigate(`/screening/clearance-rules-analyses/${response.id}`);
      }
    } catch (e: any) {
      const errorMessage = await parseErrorMessage(e);
      setStatus(errorMessage);
      dispatch(
        showFeedback({
          message: errorMessage,
          success: false,
        })
      );
    }
    setEditing(false);
    setSubmitting(false);
  };
  return (
    <Card body={false} className={'card-info'}>
      <Card.Header>
        <h4 className='card-header-title'>General</h4>
        {analysis.id && (
          <Row className='align-items-center'>
            <Has
              anyPermissions={[
                CAN_MODIFY_SCREENING_CLEARANCE_RULES_ANALYSIS,
                CAN_DELETE_SCREENING_CLEARANCE_RULES_ANALYSIS,
              ]}
            >
              {!editing && (
                <Col md={'auto'}>
                  <Button variant='white btn-sm' onClick={() => setEditing(true)}>
                    Edit general info
                  </Button>
                </Col>
              )}
              <Col>
                <DropdownWrapper>
                  <Dropdown.Toggle
                    data-testid='edit-dropdown'
                    as={Button}
                    className='btn-white btn-sm'
                    id='edit-dropdown'
                  >
                    •••
                  </Dropdown.Toggle>
                  <Dropdown.Menu className={'p-0'}>
                    <Button
                      className='dropdown-item white btn-sm'
                      onClick={() => {
                        deleteAnalysis(analysis.id as number);
                      }}
                    >
                      Delete
                    </Button>
                  </Dropdown.Menu>
                </DropdownWrapper>
              </Col>
            </Has>
          </Row>
        )}
      </Card.Header>
      <Card.Body>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={submit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, values, resetForm }: FormikProps<ScreeningClearanceRulesAnalysisInfoFormValues>) => {
            return (
              <Form>
                <Row>
                  <Col md={5}>
                    <InfoSection
                      editing={editing}
                      label={'Name'}
                      name={'name'}
                      placeholder={'Enter analysis name'}
                      value={values.name}
                    />
                  </Col>
                  <Col md={1} />
                </Row>
                <Row>
                  <Col md={5}>
                    <InfoSection
                      editing={editing}
                      label={'Description'}
                      name={'description'}
                      placeholder={'Enter analysis description'}
                      value={values.description}
                    />
                    <Col md={1} />
                  </Col>
                </Row>
                <Row>
                  <Col md={6} className='d-flex align-items-center justify-content-end'>
                    {editing && (
                      <Has
                        anyPermissions={[
                          CAN_ADD_SCREENING_CLEARANCE_RULES_ANALYSIS,
                          CAN_MODIFY_SCREENING_CLEARANCE_RULES_ANALYSIS,
                        ]}
                      >
                        {analysis.id && (
                          <Button
                            variant='outline-secondary'
                            onClick={() => {
                              resetForm();
                              setEditing(false);
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                        <Button type='submit' variant='primary' disabled={isSubmitting}>
                          Save
                        </Button>
                      </Has>
                    )}
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default ScreeningClearanceRulesAnalysisInfo;
